import { type JSX } from 'preact';
import { createContext, type ComponentChildren } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Storage key
const THEME_STORAGE_KEY = 'ots-theme-preference';

// Get system preference
function getSystemTheme(): Theme {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

// Get initial theme (system preference, with optional user override)
function getInitialTheme(): Theme {
  const systemTheme = getSystemTheme();
  const stored = localStorage.getItem(THEME_STORAGE_KEY);

  console.log('Initial theme check:', { systemTheme, storedOverride: stored });

  // If user has explicitly overridden system preference, use that
  if (stored === 'light' || stored === 'dark') {
    console.log('Using stored override:', stored);
    return stored;
  }

  // Otherwise, follow system preference
  console.log('Following system preference:', systemTheme);
  return systemTheme;
}

export function ThemeProvider({ children }: { children: ComponentChildren }): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    console.log('Applying theme:', theme, 'to document.documentElement');
    if (theme === 'dark') {
      root.classList.add('dark');
      console.log('Added dark class. Classes:', root.className);
    } else {
      root.classList.remove('dark');
      console.log('Removed dark class. Classes:', root.className);
    }
  }, [theme]);

  // Listen to system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent): void => {
      const newSystemTheme = e.matches ? 'dark' : 'light';
      const stored = localStorage.getItem(THEME_STORAGE_KEY);

      // Only sync if user hasn't explicitly overridden system preference
      if (!stored) {
        console.log('System preference changed to:', newSystemTheme);
        setThemeState(newSystemTheme);
      } else {
        console.log('System preference changed, but user has override:', stored);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setTheme = (newTheme: Theme): void => {
    setThemeState(newTheme);
    const systemTheme = getSystemTheme();

    // Only store if user is overriding system preference
    // If they toggle to match system, clear the override
    if (newTheme === systemTheme) {
      console.log('Theme matches system, removing override');
      localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      console.log('Theme differs from system, storing override:', newTheme);
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    }
  };

  const toggleTheme = (): void => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    console.log('Toggling theme from', theme, 'to', newTheme);
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
